/**
 * Mixer Blending Engine
 * WEB-REF-003 FIX: Extracted from mixer-tool.ts to reduce component size.
 *
 * Provides pure algorithmic functions for:
 * - Color blending (RGB, LAB, OKLAB, RYB, HSL, Spectral)
 * - Color distance calculation for dye matching
 * - Finding closest matching dyes to a blended color
 *
 * @module services/mixer-blending-engine
 */

import { ColorService, dyeService } from '@services/index';
import type { Dye } from '@xivdyetools/types';
import type { MixingMode, MatchingMethod } from '@shared/tool-config-types';
import type { DyeFiltersConfig } from '@shared/tool-config-types';
import { isDyeExcluded } from '@shared/dye-filter-utils';

// ============================================================================
// Types
// ============================================================================

/**
 * Result of mixing colors and finding matching dyes
 */
export interface MixedColorResult {
  blendedHex: string;
  matchedDye: Dye;
  distance: number;
}

/**
 * Configuration for the blending engine
 */
export interface BlendingConfig {
  mixingMode: MixingMode;
  matchingMethod: MatchingMethod;
  maxResults: number;
}

// ============================================================================
// Color Blending Functions
// ============================================================================

/**
 * Blend two hex colors using the specified mixing algorithm.
 *
 * Color mixing modes and their characteristics (blue + yellow at 50/50,
 * measured — the one pair that separates all six):
 * - RGB: Light mixing (additive) - #808080, grey
 * - LAB: Perceptual uniform - Blue + Yellow = Pink (LAB hue distortion)
 * - OKLAB: Modern perceptual - Blue + Yellow = Cyan (fixes LAB issues)
 * - RYB: Artist's wheel - #008000, green
 * - HSL: Hue-based blending
 * - Spectral: Kubelka-Munk physics - #398F54, green (most realistic)
 *
 * RYB used to be the Gossett-Chen paint cube and used to give olive; core
 * 5.0.0 retired that cube because it failed the identity law (mixing a dye
 * with itself did not return it, on 53% of dye pairs). Every mode here is a
 * one-line delegation to `@xivdyetools/core/blending` — core computes, this
 * app selects.
 *
 * @param hex1 First hex color
 * @param hex2 Second hex color
 * @param mixingMode The mixing algorithm to use
 * @param ratio Blend ratio (0-1, default 0.5 for equal mix)
 * @returns Blended hex color
 */
export function blendTwoColors(
  hex1: string,
  hex2: string,
  mixingMode: MixingMode,
  ratio: number = 0.5
): string {
  switch (mixingMode) {
    case 'rgb':
      return ColorService.mixColorsRgb(hex1, hex2, ratio);
    case 'lab':
      return ColorService.mixColorsLab(hex1, hex2, ratio);
    case 'oklab':
      return ColorService.mixColorsOklab(hex1, hex2, ratio);
    case 'ryb':
      return ColorService.mixColorsRyb(hex1, hex2, ratio);
    case 'hsl':
      return ColorService.mixColorsHsl(hex1, hex2, ratio);
    case 'spectral':
      return ColorService.mixColorsSpectral(hex1, hex2, ratio);
    default:
      // Default to RYB for paint-like results
      return ColorService.mixColorsRyb(hex1, hex2, ratio);
  }
}

// ============================================================================
// Color Distance Functions
// ============================================================================

/**
 * Calculate color distance using the specified matching method.
 *
 * @param hex1 First hex color
 * @param hex2 Second hex color
 * @param matchingMethod The distance algorithm to use
 * @returns Numeric distance (lower = more similar)
 */
export function calculateColorDistance(
  hex1: string,
  hex2: string,
  matchingMethod: MatchingMethod
): number {
  // 5.0: one dispatch suite-wide (dE2000 default lives in core)
  return ColorService.getDistanceForMethod(hex1, hex2, matchingMethod);
}

// ============================================================================
// Dye Matching Functions
// ============================================================================

/**
 * Find the closest matching dyes to a blended color.
 *
 * @param blendedColor The target color to match (hex)
 * @param config Blending configuration (matchingMethod, maxResults)
 * @param excludeIds Array of dye IDs to exclude from results (e.g., input dyes)
 * @param dyeFiltersConfig Optional dye filter configuration to apply user filter preferences
 * @returns Array of matched dye results sorted by distance
 */
export function findMatchingDyes(
  blendedColor: string,
  config: Pick<BlendingConfig, 'matchingMethod' | 'maxResults'>,
  excludeIds: number[] = [],
  dyeFiltersConfig?: DyeFiltersConfig | null
): MixedColorResult[] {
  const allDyes = dyeService.getAllDyes();
  const results: MixedColorResult[] = [];

  for (const dye of allDyes) {
    // Skip excluded dyes and Facewear
    if (excludeIds.includes(dye.id) || dye.category === 'Facewear') {
      continue;
    }

    // Apply user filters if available
    if (dyeFiltersConfig && isDyeExcluded(dyeFiltersConfig, dye)) {
      continue;
    }

    const distance = calculateColorDistance(blendedColor, dye.hex, config.matchingMethod);
    results.push({
      blendedHex: blendedColor,
      matchedDye: dye,
      distance,
    });
  }

  // Sort by distance and take top maxResults
  results.sort((a, b) => a.distance - b.distance);
  return results.slice(0, config.maxResults);
}

/**
 * Get contrasting text color for a background.
 * Returns black or white depending on background luminance.
 *
 * @param hex Background color in hex format
 * @returns '#000000' or '#ffffff'
 */
export function getContrastColor(hex: string): string {
  const rgb = ColorService.hexToRgb(hex);
  // Calculate luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}
